import React, { useState } from 'react';
import { Trash2, X, MousePointerClick } from 'lucide-react';
import { MondayLogoOutline } from '@vibe/icons';
import {
  ALIGN_OPTIONS,
  DEFAULT_FONT_SIZE,
  OVERFLOW_OPTIONS,
  maxLinesFor,
  overlaySizePt,
  resizeOverlayPt,
} from '../../utils/pdfOverlays';
import {
  KIND,
  PREFILL_STYLE,
  SIGNER_FIELD_TYPES,
  SIGNER_STAMP_FONT_SIZE,
  resizeSignerFieldPt,
  signerColor,
} from './sign-overlays';

/**
 * Types the signer has to answer, and the example wording each one gets. The
 * value doubles as the placeholder, so adding a type here is one line.
 */
const LABELLABLE = {
  text: 'e.g. Job title, ID number',
  date: 'e.g. Effective date, Date of birth',
  checkbox: 'e.g. I agree to the terms',
};

/**
 * Number input for one dimension, in PDF points. Ported from SmartForm's
 * PdfOverlaySettingsPanel: the draft state lets a drag flow live into the
 * field while typing is not fought (an intermediate "4" on the way to "45"
 * must not be clamped up and re-rendered under the cursor). Blur drops the
 * draft and the field snaps back to the stored truth.
 */
function SizeInput({ value, onCommit, disabled, ariaLabel }) {
  const [draft, setDraft] = useState(null);

  return (
    <div className='flex items-center gap-1.5'>
      <input
        type='number'
        min={1}
        step={1}
        aria-label={ariaLabel}
        disabled={disabled}
        value={draft ?? String(Math.round(value))}
        onChange={(e) => {
          const text = e.target.value;
          setDraft(text);
          const n = Number(text);
          // Empty or half-typed ("-", "") is not a size yet — leave the box alone.
          if (text.trim() !== '' && Number.isFinite(n)) onCommit(n);
        }}
        onBlur={() => setDraft(null)}
        onKeyDown={(e) => {
          if (e.key === 'Enter') e.currentTarget.blur();
        }}
        className='w-20 px-2 py-1 text-sm border border-gray-200 rounded-lg focus:outline-none disabled:bg-gray-50 disabled:text-gray-400'
      />
      <span className='text-xs text-gray-400'>pt</span>
    </div>
  );
}

/**
 * SignFieldPropsPanel — right pane of step 2 (§4 "Properties panel").
 *
 * Renders per selected-field type:
 *   - item data: box size (pt) / font size / alignment / overflow / remove
 *   - signature: Assigned to (reassigning recolours live) / size / required / remove
 *   - date signed: Assigned to + size + the not-editable explainer / remove
 *   - date, text: Assigned to / size / required / (text: signer-facing label) / remove
 *   - nothing selected: the §4 empty state
 *
 * Sizes are shown in PDF points (same rationale as SmartForm's
 * PdfOverlaySettingsPanel): fontSize is points, and the "Fits N lines" hint
 * divides one by the other — text always wraps and the box's height decides
 * the line budget, in the stamped document and on the signing page alike.
 */
export default function SignFieldPropsPanel({
  field,
  signers,
  columnTitles,
  prefillColumnIds,
  pagePt,
  /** Every board column a signer answer can be saved to, with its type. */
  mappingColumns = [],
  /** columnId → the field id already saving there, for the collision warning. */
  mappingClaims = new Map(),
  onChange,
  onRemove,
  onClose,
}) {
  if (!field) {
    return (
      <aside className='flex flex-col flex-shrink-0 w-72 bg-white border-l border-gray-200'>
        <div className='flex-1 flex flex-col items-center justify-center text-center px-6'>
          <MousePointerClick size={24} className='text-gray-300 mb-2' />
          <p className='text-sm text-gray-500'>Select a field on the page, or drag one from the left.</p>
        </div>
      </aside>
    );
  }

  const isSigner = field.kind === KIND.SIGNER;
  const meta = isSigner ? SIGNER_FIELD_TYPES[field.type] || SIGNER_FIELD_TYPES.text : null;
  const style = isSigner ? signerColor(field.signerIndex) : PREFILL_STYLE;
  const title = isSigner ? meta.label : columnTitles.get(field.columnId) || field.columnId;
  const orphan = !isSigner && !prefillColumnIds.has(field.columnId);

  // Geometry is stored as page fractions; points are what the owner can
  // reason about. Only available once the field's page has rendered — which
  // it has, since a field is selected by clicking it (guard, not a state).
  const sizePt = pagePt ? overlaySizePt(field, pagePt) : null;
  const fontSize = isSigner ? SIGNER_STAMP_FONT_SIZE : field.fontSize ?? DEFAULT_FONT_SIZE;
  const lineCount = sizePt ? maxLinesFor(sizePt.h, fontSize) : 1;
  // Line count only means something where a text VALUE gets stamped: item
  // data always, signer text fields at completion. Signatures are images and
  // dates are single tokens — a "Fits 3 lines" hint there is noise.
  const showsLines = !isSigner || field.type === 'text';
  const commitSize = (next) =>
    onChange(isSigner ? resizeSignerFieldPt(field, pagePt, next) : resizeOverlayPt(field, pagePt, next));

  return (
    <aside className='flex flex-col flex-shrink-0 w-72 bg-white border-l border-gray-200 overflow-y-auto'>
      <div className='flex items-center justify-between flex-shrink-0 px-4 py-3 border-b border-gray-100'>
        <div className='flex items-center gap-2 min-w-0'>
          <span className='w-2.5 h-2.5 rounded-full flex-shrink-0' style={{ background: style.color }} />
          <p className='text-sm font-semibold truncate text-gray-900'>{title}</p>
        </div>
        <button type='button' onClick={onClose} aria-label='Close' className='p-1 rounded hover:bg-gray-100'>
          <X size={15} className='text-gray-400' />
        </button>
      </div>

      <div className='flex-1 px-4 py-4 space-y-5'>
        {orphan && (
          <div className='px-3 py-2.5 text-xs rounded-lg border' style={{ borderColor: 'rgba(216,58,82,0.35)', background: 'rgba(216,58,82,0.06)', color: '#d83a52' }}>
            This board column was deleted, so nothing will be filled in here. Remove the placement
            before activating.
          </div>
        )}

        {/* ── Item-data properties ── */}
        {!isSigner && (
          <>
            <PanelField label='Value source'>
              <div className='flex items-center gap-2 px-3 py-1.5 border border-gray-200 rounded-lg bg-gray-50'>
                <MondayLogoOutline style={{ width: 12, height: 12, color: style.color, flexShrink: 0 }} />
                <span className='text-sm text-gray-900'>Item column — filled automatically</span>
              </div>
            </PanelField>

            {sizePt && (
              <>
                <PanelField label='Width'>
                  <SizeInput
                    ariaLabel='Width in points'
                    value={sizePt.w}
                    onCommit={(n) => commitSize({ w: n })}
                  />
                </PanelField>

                <PanelField label='Height'>
                  <SizeInput
                    ariaLabel='Height in points'
                    value={sizePt.h}
                    onCommit={(n) => commitSize({ h: n })}
                  />
                  <p className='text-xs text-gray-500 mt-1'>
                    Fits {lineCount} line{lineCount === 1 ? '' : 's'} at {fontSize}pt
                  </p>
                </PanelField>
              </>
            )}

            <PanelField label='Font size'>
              <input
                type='number'
                min={4}
                max={72}
                value={field.fontSize ?? DEFAULT_FONT_SIZE}
                onChange={(e) => {
                  const n = Number(e.target.value);
                  // Same clamp the server enforces — the editor never shows a
                  // value that would be silently changed on save.
                  if (Number.isFinite(n)) onChange({ fontSize: Math.min(Math.max(n, 4), 72) });
                }}
                className='w-20 px-2 py-1 text-sm border border-gray-200 rounded-lg focus:outline-none'
              />
            </PanelField>

            <PanelField label='Alignment'>
              <div className='flex overflow-hidden border border-gray-200 rounded-lg'>
                {ALIGN_OPTIONS.map(({ value, label }) => {
                  const active = (field.align || 'left') === value;
                  return (
                    <button
                      key={value}
                      type='button'
                      onClick={() => onChange({ align: value })}
                      className={`flex-1 px-3 py-1.5 text-xs font-medium transition-colors ${
                        active ? 'bg-blue-600 text-white' : 'text-gray-600 hover:bg-gray-50'
                      }`}
                    >
                      {label}
                    </button>
                  );
                })}
              </div>
            </PanelField>

            <PanelField label='If the value is too long'>
              <div className='flex flex-col gap-1'>
                {OVERFLOW_OPTIONS.map(({ value, label }) => (
                  <label key={value} className='flex items-center gap-2 cursor-pointer'>
                    <input
                      type='radio'
                      name={`overflow-${field.id}`}
                      checked={(field.overflow || 'shrink') === value}
                      onChange={() => onChange({ overflow: value })}
                    />
                    <span className='text-sm text-gray-900'>{label}</span>
                  </label>
                ))}
              </div>
              <p className='text-xs text-gray-500 mt-1'>
                Text wraps to fill the box — make it taller for more lines. This only decides what
                happens once those lines are full.
              </p>
            </PanelField>
          </>
        )}

        {/* ── Signer-field properties ── */}
        {isSigner && (
          <>
            <PanelField label='Assigned to'>
              <select
                value={field.signerIndex}
                onChange={(e) => onChange({ signerIndex: Number(e.target.value) })}
                className='w-full px-2.5 py-1.5 text-sm border border-gray-200 rounded-lg bg-white focus:outline-none'
              >
                {(signers || []).map((s, i) => (
                  <option key={i} value={i}>
                    {s.roleLabel}
                  </option>
                ))}
              </select>
              <p className='text-xs text-gray-500 mt-1'>Reassigning recolours the field on the page.</p>
            </PanelField>

            {field.type === 'date_signed' && (
              <div className='px-3 py-2.5 text-xs rounded-lg border border-gray-200 bg-gray-50 text-gray-600'>
                Stamped with the date this signer signs — not editable by the signer.
              </div>
            )}

            {/*
              Every type the signer has to ANSWER can be labelled, not just
              text. A date box asking for "Effective date" and one asking for a
              date of birth look identical on the page, and an unlabelled
              checkbox asks nothing at all — it is the type where the label does
              the most work, and it was the one type that could not have one.

              Not `signature`, whose box already reads "Sign — <role>", and not
              `date_signed`, which the server fills.
            */}
            {LABELLABLE[field.type] && (
              <PanelField label='Label shown to the signer'>
                <input
                  type='text'
                  value={field.label || ''}
                  maxLength={80}
                  placeholder={LABELLABLE[field.type]}
                  onChange={(e) => onChange({ label: e.target.value })}
                  className='w-full px-2.5 py-1.5 text-sm border border-gray-200 rounded-lg focus:outline-none'
                />
                <p className='text-xs text-gray-500 mt-1'>
                  {field.type === 'checkbox'
                    ? 'A tickbox with no label asks nothing — this is the wording the signer agrees to.'
                    : 'Shown above the box on the signing page. Never printed on the document.'}
                </p>
              </PanelField>
            )}

            {/*
              Order here is deliberate: WHO fills it in, WHAT it is, WHERE the
              answer goes, then how big the box is, then whether it is optional.
              Size sat second for a long time because it was the only thing
              worth adjusting; the mapping made the top of this panel about the
              field's meaning, and geometry is the fiddly part you come back to
              after that is settled.
            */}
            <SignerFieldMapping
              field={field}
              mappingColumns={mappingColumns}
              mappingClaims={mappingClaims}
              onChange={onChange}
            />

            {sizePt && (
              <>
                <PanelField label='Width'>
                  <SizeInput
                    ariaLabel='Width in points'
                    value={sizePt.w}
                    onCommit={(n) => commitSize({ w: n })}
                  />
                </PanelField>

                <PanelField label='Height'>
                  <SizeInput
                    ariaLabel='Height in points'
                    value={sizePt.h}
                    disabled={Boolean(meta.heightFromWidth || meta.square)}
                    onCommit={(n) => commitSize({ h: n })}
                  />
                  {meta.square ? (
                    <p className='text-xs text-gray-500 mt-1'>
                      Follows the width — a checkbox stays square.
                    </p>
                  ) : meta.heightFromWidth ? (
                    <p className='text-xs text-gray-500 mt-1'>
                      Follows the width, so the pad keeps its proportions.
                    </p>
                  ) : showsLines ? (
                    <p className='text-xs text-gray-500 mt-1'>
                      Fits {lineCount} line{lineCount === 1 ? '' : 's'} at {fontSize}pt — the
                      signer&apos;s text wraps to fill the box.
                    </p>
                  ) : null}
                </PanelField>
              </>
            )}

            {field.type !== 'date_signed' && (
              /*
                No `PanelField` heading here, unlike every other row.

                It used to have one, and the checkbox beneath it restated it as a
                sentence — "Required" over "The signer must complete this field".
                A property named twice, and a full sentence beside a checkbox
                reads as consent ("I accept the terms") rather than as a setting.
                The word IS the label, and the consequence goes where this panel
                already puts consequences: the help line.

                A signature is always required, so its box is checked and
                disabled rather than absent — the setting is still true of the
                field, and showing it greyed says so where hiding it would leave
                the question open.
              */
              <div>
                <label
                  className={`flex items-center gap-2 ${
                    field.type === 'signature' ? 'cursor-default' : 'cursor-pointer'
                  }`}
                >
                  <input
                    type='checkbox'
                    checked={field.required !== false}
                    disabled={field.type === 'signature'}
                    onChange={(e) => onChange({ required: e.target.checked })}
                  />
                  {/* Exactly the `PanelField` label style — this row has no
                      heading of its own, so the word has to carry that weight
                      and sit level with "Alignment" and the rest. */}
                  <span
                    className={`text-xs font-medium ${
                      field.type === 'signature' ? 'text-gray-400' : 'text-gray-500'
                    }`}
                  >
                    Required
                  </span>
                </label>
                {/* Describes what the SETTING does, not which way it is set —
                    so it does not change under the box as you tick it. The
                    checkbox already shows the state; this says what the state
                    means. */}
                <p className='text-xs text-gray-500 mt-1'>
                  {field.type === 'signature'
                    ? 'Signature fields are always required'
                    : 'Signers must fill to submit document'}
                </p>
              </div>
            )}
          </>
        )}

        <div className='pt-2 border-t border-gray-100'>
          <button
            type='button'
            onClick={onRemove}
            className='flex items-center gap-1.5 text-sm font-medium text-red-600'
          >
            <Trash2 size={14} />
            Remove this field
          </button>
        </div>
      </div>
    </aside>
  );
}

/**
 * "Save to monday column" — where this field's answer lands on the board.
 *
 * The mirror of the item-data fields above: those read a column INTO the
 * document, this writes one back OUT of it when the request completes.
 *
 * Offered per type, filtered to `mappingTargets` (sign-overlays.js). One field
 * type, one column type, so the list is short and every entry in it will
 * actually accept the value — a `text` field cannot offer a date column and
 * then fail at completion, days later, with nobody watching.
 *
 * `date_signed` is included even though the signer never touches it: the server
 * stamps the date, and "when did they sign" is one of the most useful things to
 * have on the board.
 */
function SignerFieldMapping({ field, mappingColumns, mappingClaims, onChange }) {
  const meta = SIGNER_FIELD_TYPES[field.type];
  const targets = meta?.mappingTargets || [];
  if (!targets.length) return null;

  const options = (mappingColumns || []).filter((c) => targets.includes(c.type));
  const selected = field.mappingColumnId || '';
  // Another field already saving to this column. A board column holds one
  // value, so the second write would erase the first — the send gate blocks it,
  // and this says so while the choice is still on screen.
  const clash = selected && mappingClaims.get(selected) && mappingClaims.get(selected) !== field.id;
  // Chosen, then the column was deleted from the board.
  const orphan = selected && !options.some((c) => c.id === selected);

  return (
    <PanelField label='Save to monday column'>
      <select
        value={selected}
        onChange={(e) => {
          const columnId = e.target.value;
          const column = options.find((c) => c.id === columnId);
          // Both halves or neither — the server drops a mapping carrying only
          // one, since the write runs at completion with no session to look the
          // type up again.
          onChange(
            column
              ? { mappingColumnId: column.id, mappingColumnType: column.type }
              : { mappingColumnId: null, mappingColumnType: null },
          );
        }}
        className='w-full px-2.5 py-1.5 text-sm border border-gray-200 rounded-lg bg-white focus:outline-none'
      >
        {/* A dash, not a sentence. Every other option in this list is a
            column name, so a phrase read as a fourth column with a strange
            name rather than as "none of them". */}
        <option value=''>—</option>
        {orphan && <option value={selected}>Deleted column</option>}
        {options.map((col) => (
          <option key={col.id} value={col.id}>
            {col.title}
          </option>
        ))}
      </select>

      {orphan ? (
        <p className='text-xs mt-1' style={{ color: '#d83a52' }}>
          That column was deleted from the board — pick another, or stop saving it.
        </p>
      ) : clash ? (
        <p className='text-xs mt-1' style={{ color: '#d83a52' }}>
          Another field already saves here. A column holds one value, so only one
          field can write to it.
        </p>
      ) : selected ? (
        <p className='text-xs text-gray-500 mt-1'>
          Written when everyone has signed — not before.
        </p>
      ) : options.length ? (
        <p className='text-xs text-gray-500 mt-1'>
          {field.type === 'date_signed'
            ? 'Put the date they signed on the item.'
            : 'Put what the signer enters on the item.'}
        </p>
      ) : (
        <p className='text-xs text-gray-500 mt-1'>
          This board has no {targets[0] === 'boolean' ? 'checkbox' : targets[0]} column to save
          it to.
        </p>
      )}
    </PanelField>
  );
}

function PanelField({ label, children }) {
  return (
    <div>
      <p className='text-xs font-medium text-gray-500 mb-1.5'>{label}</p>
      {children}
    </div>
  );
}
